'use client';

import { captureError } from '@zealot/monitoring/browser';
import { motion } from 'framer-motion';
import { AlertTriangle, Camera } from 'lucide-react';
import React from 'react';
import { isChunkLoadError, reloadForChunkError } from '@/lib/utils/chunkReload';
import { isEmbedMode } from '@/lib/utils/iframe';
import logger from '@/utils/logger';

/**
 * Application-wide error boundary for the advocate portal.
 *
 * This is the single error boundary for the app. It is mounted once at the root
 * of `_app.jsx`, wrapping every provider and every page, and catches any
 * render-time exception that escapes the React tree below it (the "Oops!
 * Something went wrong" screen). React Query failures do NOT reach it — the
 * query client runs with `throwOnError: false` — so anything caught here is a
 * genuine render crash.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    // Flag chunk-load errors so render() shows a loader, not the crash screen,
    // while componentDidCatch reloads.
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    // Reload once to re-fetch a failed chunk; if suppressed by the cooldown,
    // fall through to the crash UI instead of looping.
    if (isChunkLoadError(error)) {
      if (reloadForChunkError()) return;
      this.setState({ isChunkError: false, error, errorInfo });
      return;
    }

    // Report to monitoring backend
    try {
      captureError(error, {
        componentStack: errorInfo.componentStack,
        source: 'ErrorBoundary',
      });
    } catch (reportError) {
      logger.error(reportError);
    }

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  componentDidUpdate(prevProps) {
    // Auto-recover when the user navigates to a different route. Without this a
    // crash on one page keeps the whole app on the error screen, because the
    // boundary sits above the router and _app never remounts on navigation.
    if (this.state.hasError && prevProps.routeKey !== this.props.routeKey) {
      this.setState({ hasError: false, error: null, errorInfo: null, isChunkError: false });
    }
  }

  handleReset = () => {
    // Recover in place: clear the error and re-render the subtree. Never wipe
    // storage or force a re-login — a transient crash recovers, a deterministic
    // one simply re-trips the boundary (the correct, contained outcome).
    this.setState({ hasError: false, error: null, errorInfo: null, isChunkError: false });
  };

  render() {
    if (this.state.hasError) {
      const isEmbed = isEmbedMode();

      // In embed mode, silently swallow the error — don't show a broken state
      if (isEmbed) {
        return null;
      }

      // Loader while the chunk reload is in flight (see componentDidCatch).
      if (this.state.isChunkError) {
        return (
          <div className="fixed inset-0 flex items-center justify-center bg-[#0D0B1F]">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white/80" />
          </div>
        );
      }

      return (
        <div className="fixed inset-0 overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 bg-[#0D0B1F]" />
          <div className="absolute inset-0 bg-linear-to-tr from-purple-900/30 via-transparent to-indigo-900/30" />
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-purple-500/30 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-indigo-500/30 rounded-full blur-[120px]" />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="relative max-w-2xl w-full px-6"
          >
            <div className="backdrop-blur-2xl bg-white/8 rounded-3xl shadow-2xl overflow-hidden border border-white/5">
              <div className="p-12 relative">
                <div className="absolute inset-0 bg-linear-to-b from-white/5 to-transparent" />

                <div className="relative">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                      duration: 0.8,
                      type: 'spring',
                      stiffness: 100,
                      delay: 0.2,
                    }}
                    className="w-28 h-28 mx-auto mb-8 relative"
                  >
                    <div className="absolute inset-0 bg-linear-to-r from-purple-500/40 to-indigo-500/40 rounded-full blur-2xl" />
                    <div className="absolute inset-0 bg-linear-to-r from-purple-500/40 to-indigo-500/40 rounded-full blur-md" />
                    <AlertTriangle className="w-full h-full text-white/90 relative drop-shadow-2xl" />
                  </motion.div>

                  <motion.h2
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-3xl font-bold text-center text-white/90 mb-4 tracking-tight"
                  >
                    Oops! Something went wrong
                  </motion.h2>

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-lg text-white/70 text-center mb-6"
                  >
                    {isEmbed
                      ? "Don't worry, it's not you - it's us! Please try refreshing the page."
                      : "Don't worry, it's not you - it's us! Please try again."}
                  </motion.p>

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="text-sm text-white/60 text-center mb-10"
                  >
                    <Camera className="inline-block mr-2 mb-1" size={16} />
                    It would be super helpful if you could screenshot the technical details below and send them to our
                    support team. They love a good puzzle!
                  </motion.p>

                  {!isEmbed && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                      className="flex justify-center"
                    >
                      <button
                        type="button"
                        onClick={this.handleReset}
                        className="px-8 py-3 bg-white/10 text-white rounded-full
                                 backdrop-blur-xs transition-all duration-300
                                 hover:bg-white/20 hover:shadow-lg hover:shadow-purple-500/10
                                 focus:outline-hidden focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-[#0D0B1F]"
                      >
                        <span className="relative font-medium tracking-wide text-white/90">Try again</span>
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="px-8 py-6 border-t border-white/8 relative"
              >
                <div className="absolute inset-0 bg-black/20 backdrop-blur-md" />
                <details className="relative text-sm text-white/60">
                  <summary className="cursor-pointer font-medium hover:text-white/90 transition-colors">
                    Technical Details (Our support team would love to see these!)
                  </summary>
                  <pre className="mt-4 p-4 bg-black/30 rounded-xl text-xs overflow-auto text-white/50 font-mono">
                    {this.state.error?.toString()}
                    <br />
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              </motion.div>
            </div>
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
